import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as mpApi from "@/api/matchProperties.api";
import type { MatchProperty } from "@/types";

interface MatchPropertiesState {
  items: MatchProperty[];
  loading: boolean;
  error: string | null;
}

const initialState: MatchPropertiesState = {
  items: [],
  loading: false,
  error: null,
};

export const fetchMatchProperties = createAsyncThunk(
  "matchProperties/fetchAll",
  async () => mpApi.getMatchProperties(),
);

export const createMatchProperty = createAsyncThunk(
  "matchProperties/create",
  async (data: Partial<MatchProperty>) => mpApi.createMatchProperty(data),
);

export const updateMatchProperty = createAsyncThunk(
  "matchProperties/update",
  async ({ id, data }: { id: string; data: Partial<MatchProperty> }) =>
    mpApi.updateMatchProperty(id, data),
);

export const deleteMatchProperty = createAsyncThunk(
  "matchProperties/delete",
  async (id: string) => {
    await mpApi.deleteMatchProperty(id);
    return id;
  },
);

const matchPropertiesSlice = createSlice({
  name: "matchProperties",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatchProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatchProperties.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchMatchProperties.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch match properties";
      })
      .addCase(createMatchProperty.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateMatchProperty.fulfilled, (state, action) => {
        const idx = state.items.findIndex((m) => m.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(deleteMatchProperty.fulfilled, (state, action) => {
        state.items = state.items.filter((m) => m.id !== action.payload);
      });
  },
});

export default matchPropertiesSlice.reducer;
